import { useState, useEffect, useRef, useCallback } from "react";
import styles from "./StoryViewer.module.css";
import useDashboardStore from "../../store/dashboardStore";
import useAuthStore from "../../store/authStore";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

function buildCaptionSegments(text) {
  const parts = text.split(URL_REGEX);
  return parts.map((part, i) =>
    URL_REGEX.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className={styles.captionLink}
        onClick={(e) => e.stopPropagation()}
      >
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

const formatViewedAt = (viewed_at) => {
  // MySQL UTC_TIMESTAMP() → "YYYY-MM-DD HH:MM:SS" (no T, no Z).
  // Replace space with T and force Z so Date always parses as UTC.
  const s = viewed_at.trim().replace(" ", "T");
  return new Date(s.endsWith("Z") ? s : s + "Z").toLocaleString();
};

const getInitials = (name) => {
  if (!name) return "?";
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
};

const IMAGE_DURATION = 5000;

const StoryViewer = () => {
  const { viewerStories, storiesViewerStartIndex, setShowStoriesViewer, addViewedStoryId } =
    useDashboardStore();
  const { user, userMeta } = useAuthStore();
  const isAdmin = user?.roles?.[0] === "administrator";

  const stories = viewerStories;
  const [currentIndex, setCurrentIndex] = useState(storiesViewerStartIndex);
  const [progress, setProgress] = useState(0);

  // Seen by
  const [viewerCount, setViewerCount] = useState(null); // for the button label
  const [seenByOpen, setSeenByOpen] = useState(false);
  const [viewers, setViewers] = useState([]);
  const [viewersLoading, setViewersLoading] = useState(false);
  const [sheetHeight, setSheetHeight] = useState(45); // percent, 45–85

  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const touchStartYRef = useRef(null);
  const currentIndexRef = useRef(currentIndex);
  currentIndexRef.current = currentIndex;

  const close = useCallback(() => {
    clearInterval(timerRef.current);
    setShowStoriesViewer(false);
  }, [setShowStoriesViewer]);

  const goNext = useCallback(() => {
    clearInterval(timerRef.current);
    if (currentIndexRef.current >= stories.length - 1) {
      setShowStoriesViewer(false);
    } else {
      setCurrentIndex((i) => i + 1);
    }
  }, [stories.length, setShowStoriesViewer]);

  const goPrev = useCallback(() => {
    clearInterval(timerRef.current);
    if (currentIndexRef.current > 0) {
      setCurrentIndex((i) => i - 1);
    }
  }, []);

  const startTimer = useCallback(
    (duration) => {
      clearInterval(timerRef.current);
      setProgress(0);
      const tickMs = 100;
      const totalTicks = duration / tickMs;
      let ticks = 0;
      timerRef.current = setInterval(() => {
        ticks++;
        const pct = (ticks / totalTicks) * 100;
        if (pct >= 100) {
          clearInterval(timerRef.current);
          setProgress(100);
          goNext();
        } else {
          setProgress(pct);
        }
      }, tickMs);
    },
    [goNext]
  );

  // Reset + start whenever index changes
  useEffect(() => {
    clearInterval(timerRef.current);
    setProgress(0);
    setSeenByOpen(false);
    setViewerCount(null);

    const story = stories[currentIndex];
    if (!story) return;

    if (story.type !== "video") {
      startTimer(IMAGE_DURATION);
    }

    // Fire-and-forget: mark as viewed
    const name = user?.display_name || user?.username || "";
    const avatar_url = userMeta?.profile_picture_url || null;
    api.post(ENDPOINTS.VIEW_STORY(story.id), { name, avatar_url }).catch(() => {});
    addViewedStoryId(story.id);

    // Background fetch viewer count for the button (admin only)
    if (isAdmin) {
      api.get(ENDPOINTS.GET_STORY_VIEWS(story.id))
        .then(({ data }) => {
          if (data.success) setViewerCount(data.viewers?.length ?? 0);
        })
        .catch(() => {});
    }

    return () => clearInterval(timerRef.current);
  }, [currentIndex, stories, startTimer, user, userMeta, isAdmin]);

  // --- Seen by ---
  const openSeenBy = async () => {
    const storyId = stories[currentIndexRef.current]?.id;
    if (!storyId) return;
    clearInterval(timerRef.current);
    setSheetHeight(45);
    setSeenByOpen(true);
    setViewers([]);
    setViewersLoading(true);
    try {
      const { data } = await api.get(ENDPOINTS.GET_STORY_VIEWS(storyId));
      if (data.success) {
        setViewers(data.viewers || []);
        setViewerCount(data.viewers?.length ?? 0);
      }
    } catch {
      // silently fail
    } finally {
      setViewersLoading(false);
    }
  };

  const closeSeenBy = () => {
    setSeenByOpen(false);
    const currentStory = stories[currentIndexRef.current];
    if (currentStory && currentStory.type !== "video") {
      startTimer(IMAGE_DURATION);
    }
  };

  // Drag-to-resize the sheet handle
  const handleHandlePointerDown = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startHeight = sheetHeight;

    const onMove = (ev) => {
      const delta = startY - ev.clientY; // drag up = positive
      const pct = (delta / window.innerHeight) * 100;
      setSheetHeight(Math.min(85, Math.max(45, startHeight + pct)));
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  // --- Video handlers ---
  const handleCanPlay = () => {
    const video = videoRef.current;
    if (!video) return;
    video.play().catch(() => {});
    const duration = Math.min(video.duration * 1000, 120000);
    startTimer(duration);
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    const maxDuration = Math.min(video.duration, 120);
    setProgress(Math.min((video.currentTime / maxDuration) * 100, 100));
  };

  const handleVideoEnded = () => {
    clearInterval(timerRef.current);
    goNext();
  };

  // --- Swipe down to close ---
  const handleTouchStart = (e) => {
    touchStartYRef.current = e.touches[0].clientY;
  };
  const handleTouchEnd = (e) => {
    if (touchStartYRef.current === null) return;
    const delta = e.changedTouches[0].clientY - touchStartYRef.current;
    if (delta > 80) close();
    touchStartYRef.current = null;
  };

  // --- Keyboard support ---
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "Escape") {
        if (seenByOpen) closeSeenBy();
        else close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev, close, seenByOpen]);

  if (!stories.length) return null;

  const story = stories[currentIndex];

  return (
    <div
      className={styles.overlay}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Progress bars */}
      <div className={styles.progressRow}>
        {stories.map((_, i) => (
          <div key={i} className={styles.progressSegment}>
            <div
              className={styles.progressFill}
              style={{
                width:
                  i < currentIndex
                    ? "100%"
                    : i === currentIndex
                    ? `${progress}%`
                    : "0%",
              }}
            />
          </div>
        ))}
      </div>

      {/* Close button */}
      <button className={styles.closeBtn} onClick={close}>
        ✕
      </button>

      {/* Content */}
      <div className={styles.contentArea}>
        {story.type === "text" ? (
          <div className={styles.textStory}>
            <p className={styles.textContent}>
              {buildCaptionSegments(story.content)}
            </p>
          </div>
        ) : story.type === "video" ? (
          <video
            key={story.id}
            ref={videoRef}
            src={story.content}
            className={styles.videoMedia}
            playsInline
            onCanPlay={handleCanPlay}
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleVideoEnded}
          />
        ) : (
          <img
            key={story.id}
            src={story.content}
            alt="Story"
            className={styles.imageMedia}
          />
        )}
      </div>

      {/* Caption overlay */}
      {story.caption?.trim() && (
        <div className={styles.captionBar}>
          <div className={styles.captionScroll}>
            <p className={styles.captionText}>
              {buildCaptionSegments(story.caption)}
            </p>
          </div>
        </div>
      )}

      {/* Seen by button — admin only */}
      {isAdmin && (
        <button
          className={styles.seenByBtn}
          onClick={(e) => {
            e.stopPropagation();
            openSeenBy();
          }}
        >
          <span className={styles.seenByIcon}>👁</span>
          <span>
            Seen by{viewerCount !== null && viewerCount > 0 ? ` ${viewerCount}` : ""}
          </span>
        </button>
      )}

      {/* Tap zones */}
      <div className={styles.tapLeft} onClick={goPrev} />
      <div className={styles.tapRight} onClick={goNext} />

      {/* Seen by panel */}
      {seenByOpen && (
        <div className={styles.viewersBackdrop} onClick={closeSeenBy}>
          <div
            className={styles.viewersSheet}
            style={{ height: `${sheetHeight}%` }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Draggable handle */}
            <div
              className={styles.viewersHandle}
              onPointerDown={handleHandlePointerDown}
            />
            <div className={styles.viewersHeader}>
              <span className={styles.viewersTitle}>
                Seen by{viewerCount !== null && viewerCount > 0 ? ` ${viewerCount}` : ""}
              </span>
              <button className={styles.viewersClose} onClick={closeSeenBy}>
                ✕
              </button>
            </div>
            <div className={styles.viewersBody}>
              {viewersLoading ? (
                <div className={styles.viewersSpinnerWrap}>
                  <div className={styles.viewersSpinner} />
                </div>
              ) : viewers.length === 0 ? (
                <p className={styles.viewersEmpty}>
                  No one has seen this story yet
                </p>
              ) : (
                <ul className={styles.viewerList}>
                  {viewers.map((viewer) => (
                    <li key={viewer.user_id} className={styles.viewerRow}>
                      <div className={styles.viewerAvatar}>
                        {viewer.avatar_url ? (
                          <img
                            src={viewer.avatar_url}
                            alt={viewer.name}
                            className={styles.viewerAvatarImg}
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextSibling.style.display = "flex";
                            }}
                          />
                        ) : null}
                        <span
                          className={styles.viewerInitials}
                          style={viewer.avatar_url ? { display: "none" } : {}}
                        >
                          {getInitials(viewer.name)}
                        </span>
                      </div>
                      <div className={styles.viewerInfo}>
                        <span className={styles.viewerName}>{viewer.name}</span>
                        <span className={styles.viewerTime}>
                          {formatViewedAt(viewer.viewed_at)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoryViewer;
